<template>
    <div class="container mx-auto">
        <steps-viewer
            :steps="$data.$steps"
            :step="currentStep"
            @click="clickStep"
        />
        <div
            v-if="currentStep === 0"
            class="w-full md:w-10/12 lg:w-8/12 mx-auto"
        >
            <div class="flex flex-col justify-center">
                <div class="flex items-center justify-center mb-4">
                    <span class="display-4 mr-2">🗃</span>
                    <p
                        class="text-theme-primary text-xl font-semibold"
                        data-testid="default-typography"
                    >
                        Sube el archivo con los productos
                    </p>
                </div>

                <div
                    class="flex items-center justify-between p-4 border rounded-xl mb-4"
                >
                    <div class="flex-1 text-sm text-theme-base pr-4">
                        <p data-testid="default-typography">
                            Completa el archivo usando la plantilla oficial. Los
                            productos se cargan para todo el
                            {{ $siteType }}. Máximo <strong>1000 filas</strong>.
                        </p>
                    </div>
                    <a
                        href="/template_massive_load_products_and_references.xlsx"
                        target="_blank"
                        rel="noopener"
                        class="flex-shrink-0 flex items-center gap-2 px-4 py-2 border-2 border-theme-success text-theme-success font-semibold rounded-lg hover:bg-theme-hover transition-colors text-sm"
                    >
                        ⬇ Descargar plantilla
                    </a>
                </div>

                <products-file-loader
                    @loaded="loadProducts"
                    @loading="isLoading = true"
                    @error="isLoading = false"
                />
            </div>
        </div>
        <div v-else-if="currentStep === 1" class="flex flex-col">
            <div class="flex flex-col items-center">
                <p data-testid="default-typography" class="my-4 font-bold">
                    Tienes errores en el archivo. Puedes corregir el archivo y
                    volver al paso anterior, o descartar las filas con error y
                    continuar con las filas válidas.
                </p>
                <div class="flex gap-3">
                    <app-button relief @click="resetItem">
                        Volver al paso anterior
                    </app-button>
                    <app-button warning @click="discardErrorRowsAndContinue">
                        Descartar filas con error y continuar
                    </app-button>
                </div>
            </div>
            <error-viewer
                v-for="(errorItem, index) in errors"
                :key="index"
                :error="errorItem"
                :active="activeError"
                :base="$data.$errorsBase"
                @changeActive="changeActiveError($event)"
            />
        </div>
        <div v-else-if="currentStep === 2">
            <div class="my-3">
                <span>
                    <strong> Resumen: </strong>
                    productos a ingresar
                    {{ productsToInsert.length }} productos ya creados
                    {{ productsAlreadyCreated.length }}
                </span>
            </div>
            <div v-if="productsToInsert.length">
                <div class="mb-4">
                    <span class="text-lg">
                        Productos a insertar
                        <span class="text-theme-primary">
                            {{ productsToInsert.length }}
                        </span>
                    </span>
                </div>

                <product-with-references-manage
                    v-for="(item, index) in productsToInsert"
                    :key="'A' + index"
                    :product="item"
                    updates-enabled
                    @remove="removeProductToInsert"
                    @update="updateProductToInsert"
                />
            </div>
            <div v-if="productsAlreadyCreated.length">
                <div class="my-4">
                    <span class="text-lg">
                        Productos ya creados
                        <span class="text-theme-primary">
                            {{ productsAlreadyCreated.length }}
                        </span>
                    </span>
                </div>

                <product-with-references-manage
                    v-for="(item, index) in productsAlreadyCreated"
                    :key="'B' + index"
                    :updates-enabled="updatesEnabled"
                    :product="item"
                    @remove="removeProductToUpdate"
                    @update="updateProductToUpdate"
                />
            </div>
            <div class="my-4 flex justify-center">
                <app-button
                    class="mt-3 mb-5 w-3/4 mx-auto"
                    border
                    gradient
                    @click="saveMassiveProductsComponent"
                >
                    <span class="text-lg"> Guardar cambios ✔ </span>
                </app-button>
            </div>
        </div>
        <div v-else-if="[3, 4].includes(currentStep)">
            <div v-if="isLoading">Procesando ...</div>
            <div v-else class="flex flex-wrap">
                <div class="w-full mb-2">
                    <span class="text-theme-primary text-xl">Resultados</span>
                </div>
                <div class="w-1/2 mt-2">
                    <strong class="h5">
                        Productos y referencias creadas :
                        <span class="text-theme-primary">
                            {{ results.productsAndReferencesCreated.length }}
                        </span>
                    </strong>
                </div>
                <div class="w-1/2 mt-2">
                    <strong class="h5">
                        Productos actualizados y referencias creadas :
                        <span class="text-theme-primary">
                            {{
                                results.productsUpdatedAndReferencesCreated
                                    .length
                            }}
                        </span>
                    </strong>
                </div>
                <div class="w-1/2 mt-2">
                    <strong class="h5">
                        Productos actualizados y referencias actualizadas :
                        <span class="text-theme-primary">
                            {{ results.productsAndReferencesUpdated.length }}
                        </span>
                    </strong>
                </div>
                <div class="w-1/2 mt-2">
                    <strong class="h5">
                        Productos NO actualizados y referencias creadas :
                        <span class="text-theme-primary">
                            {{
                                results.productsNotUpdatedAndReferencesCreated
                                    .length
                            }}
                        </span>
                    </strong>
                </div>
                <div class="w-1/2 mt-2">
                    <strong class="h5">
                        Productos NO actualizados y referencias actualizadas :
                        <span class="text-theme-primary">
                            {{
                                results.productsNotUpdatedAndReferencesUpdated
                                    .length
                            }}
                        </span>
                    </strong>
                </div>
                <div class="w-full flex justify-center mt-4">
                    <app-button @click="resetItem">
                        Iniciar nueva carga
                    </app-button>
                    <app-button success @click="downloadJsonComponent">
                        Descargar Resultados JSON ⬇
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { downloadJson } from "@/common/lib/json";

import ProductWithReferencesManage from "@/components/products/ProductWithReferencesManage.vue";
import ProductsFileLoader from "@/components/products/ProductsFileLoader.vue";
import ErrorViewer from "@/components/utils/ErrorViewer.vue";
import StepsViewer from "@/components/utils/StepsViewer.vue";

export default {
    name: "MassiveLoadProducts",
    components: {
        ProductsFileLoader,
        StepsViewer,
        ErrorViewer,
        ProductWithReferencesManage
    },
    data: () => ({
        isLoading: false,
        updatesEnabled: false,
        lastRows: [],
        step: 0,
        $steps: [
            { name: "Sube archivo", enabledClicked: true },
            { name: "Valida errores en archivo" },
            { name: "Verifica los productos" },
            { name: "Guarda los cambios" }
        ],
        $errorsBase: [
            { name: 'Nombre de producto "name"', key: "name" },
            { name: 'Descripción "summary"', key: "summary" },
            { name: 'SKU "sku"', key: "sku" },
            { name: 'Código de barras "barcode"', key: "barcode" },
            { name: 'Tamaño/Talla/Valor "size"', key: "size" },
            { name: 'Color referencia "color"', key: "color" },
            { name: 'Código de color "colorRef"', key: "colorRef" },
            { name: 'Precio de venta "price"', key: "price" },
            { name: 'Costo "cost"', key: "cost" }
        ],
        currentStep: 0,
        activeError: 0,
        productsToInsert: [],
        productsAlreadyCreated: [],
        errors: [],
        results: {
            productsAndReferencesCreated: [],
            productsAndReferencesUpdated: [],
            productsUpdatedAndReferencesCreated: [],
            productsNotUpdatedAndReferencesCreated: [],
            productsNotUpdatedAndReferencesUpdated: []
        }
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"])
    },
    watch: {
        selectedMerchant() {
            this.resetItem();
        }
    },
    async mounted() {
        this.updatesEnabled =
            this.$route.name === "ProductsMassiveLoadAndUpdates";
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        await verifyPagePermission("product:LoadMassive", "/app/products");
    },
    beforeDestroy() {},
    methods: {
        ...mapActions("products", [
            "verifyMassiveProducts",
            "saveMassiveProducts"
        ]),
        async loadProducts(rows) {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: `Vas a cargar ${rows.length} ${
                    rows.length < 2 ? "fila" : "filas"
                } ?`,
                icon: "info",
                showCancelButton: true,
                focusConfirm: false,
                confirmButtonText: "Si",
                cancelButtonText: "No",
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#d33ddd",
                allowOutsideClick: false
            });
            if (!dialogResult.isConfirmed) {
                window.location.href = this.$route.path;
                return;
            }
            this.lastRows = rows;
            this.verifyMassiveProductsComponent(rows);
        },
        async verifyMassiveProductsComponent(rows) {
            this.errors = [];
            this.productsAlreadyCreated = [];
            this.productsToInsert = [];
            this.isLoading = true;
            this.$loading.show();
            try {
                const result = await this.verifyMassiveProducts({
                    rows
                });
                this.isLoading = false;
                this.$loading.hide();
                if (!result.pass) {
                    this.errors = result.errors;
                    this.activeError = result.errors[0].index;
                    this.currentStep = 1;
                    return;
                }
                this.currentStep = 2;
                this.productsAlreadyCreated = result.productsAlreadyCreated;
                this.productsToInsert = result.productsToInsert;

                if (result.productsAlreadyCreated.length > 0) {
                    if (this.updatesEnabled) {
                        this.$swal.fire({
                            background: this.backgroundColor,
                            title: "Hemos encontrado productos anteriormente registrados",
                            html: `<p class="text-justify">Recuerda que se va a actualizar productos y referencias ya creadas.</p>`,
                            icon: "info",
                            showCloseButton: true
                        });
                        return;
                    }

                    const dialogResult = await this.$swal.fire({
                        background: this.backgroundColor,
                        title: "Hemos encontrado productos anteriormente registrados",
                        html: `<p class="text-justify">Recuerda que se va a omitir los productos y referencias ya creados, usa las herramientas de productos para actaulizar la información. <br /><br />Los productos con referencias nuevas si serán cargados. Deseas que se muestren los registros que se omitirán ?                   
                        </p>`,
                        icon: "info",
                        showCancelButton: true,
                        confirmButtonText: "Eliminar lo que se omitirá",
                        cancelButtonText: "Mostrar todo",
                        confirmButtonColor: "#02672D",
                        cancelButtonColor: "#113ea2",
                        allowOutsideClick: false,
                        showCloseButton: true
                    });
                    if (dialogResult.isConfirmed) {
                        this.productsAlreadyCreated =
                            this.filterProductsAlreadyCreated(
                                result.productsAlreadyCreated
                            );
                        return;
                    }
                    this.productsAlreadyCreated = result.productsAlreadyCreated;
                }
            } catch (error) {
                this.isLoading = false;

                this.$loading.hide();
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                if (error.statusCode === 404) {
                    this.$router.push("/app/products");
                }
            }
        },
        async saveMassiveProductsComponent() {
            const productsAlreadyCreated = this.updatesEnabled
                ? this.productsAlreadyCreated
                : this.filterProductsAlreadyCreated(
                      this.productsAlreadyCreated
                  );

            const totalRecords =
                this.productsToInsert.length + productsAlreadyCreated.length;

            if (!totalRecords) {
                const dialogResult = await this.$swal.fire({
                    title: "No hay registros que guardar. Deseas volver al paso inicial ?",
                    background: this.backgroundColor,
                    icon: "alert",
                    showCancelButton: true,
                    focusConfirm: false,
                    confirmButtonText: "No hacer nada",
                    cancelButtonText: "Volver al paso inicial",
                    confirmButtonColor: "#02672D",
                    cancelButtonColor: "#113ea2",
                    allowOutsideClick: false,
                    showCloseButton: true
                });
                if (!dialogResult.isConfirmed) {
                    location.reload();
                    return;
                }
                return;
            }
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: `Vas a insertar ${this.productsToInsert.length} productos y a actualizar ${productsAlreadyCreated.length}?`,
                icon: "info",
                showCancelButton: true,
                focusConfirm: false,
                confirmButtonText: "Si",
                cancelButtonText: "No",
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#d33ddd",
                allowOutsideClick: false
            });
            if (!dialogResult.isConfirmed) {
                return;
            }
            this.productsAlreadyCreated = productsAlreadyCreated;
            this.isLoading = true;
            this.$loading.show();
            this.currentStep = 3;
            const products = [
                ...this.productsToInsert,
                ...productsAlreadyCreated
            ].map((item, index) => ({
                index: index + 1,
                item: {
                    sku: item.sku,
                    name: item.name,
                    summary: item.summary,
                    brandSlug: item.brand.slug,
                    lineSlug: item.subline.slug,
                    references: [
                        ...item.referencesToInsert,
                        ...item.referencesAlreadyCreated
                    ].map((item2) => ({
                        barcode: item2.barcode,
                        price: item2.price,
                        priceOffer: item2.priceOffer,
                        cost: item2.cost,
                        color: item2.color,
                        colorRef: item2.colorRef,
                        size: item2.size,
                        images: item2.images.map((item3) => ({
                            id: item3.id,
                            sequence: item3.sequence.sequence
                        }))
                    }))
                }
            }));
            try {
                this.results = await this.saveMassiveProducts({
                    products,
                    needUpdateProducts: this.updatesEnabled
                });
                this.$analytics.trackEvent("products_bulk_uploaded", {
                    products_count: products.length,
                    updates_enabled: this.updatesEnabled
                });
                this.currentStep = 4;
                this.$sound.play();
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.isLoading = false;
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                this.currentStep = 3;
            }
        },
        filterProductsAlreadyCreated(productsAlreadyCreated = []) {
            return productsAlreadyCreated
                .filter((item) => item.referencesToInsert.length > 0)
                .map((item) => ({ ...item, referencesAlreadyCreated: [] }));
        },
        clickStep(step) {
            this.currentStep = step;
        },
        changeActiveError(ref) {
            this.activeError = ref;
        },
        async discardErrorRowsAndContinue() {
            const errorIndexes = new Set(this.errors.map((e) => e.index));
            const filteredRows = this.lastRows.filter(
                (r) => !errorIndexes.has(r.index)
            );
            if (!filteredRows.length) {
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "No quedan filas válidas para procesar",
                    icon: "info"
                });
                this.resetItem();
                return;
            }
            this.verifyMassiveProductsComponent(filteredRows);
        },
        resetItem() {
            this.errors = [];
            this.productsToInsert = [];
            this.productsAlreadyCreated = [];
            this.lastRows = [];
            this.currentStep = 0;
        },
        removeProductToInsert({ sku }) {
            const index = this.productsToInsert.findIndex(
                (item) => item.sku === sku
            );
            let productsToInsert = [...this.productsToInsert];
            productsToInsert.splice(index, 1);
            this.productsToInsert = [...productsToInsert];
        },
        updateProductToInsert(payload) {
            const index = this.productsToInsert.findIndex(
                (item) => item.sku === payload.sku
            );
            let productsToInsert = [...this.productsToInsert];
            productsToInsert[index] = {
                ...payload
            };
            this.productsToInsert = [...productsToInsert];
        },
        removeProductToUpdate({ sku }) {
            const index = this.productsAlreadyCreated.findIndex(
                (item) => item.sku === sku
            );
            let productsAlreadyCreated = [...this.productsAlreadyCreated];
            productsAlreadyCreated.splice(index, 1);
            this.productsAlreadyCreated = [...productsAlreadyCreated];
        },
        updateProductToUpdate(payload) {
            const index = this.productsAlreadyCreated.findIndex(
                (item) => item.sku === payload.sku
            );
            let productsAlreadyCreated = [...this.productsAlreadyCreated];
            productsAlreadyCreated[index] = {
                ...payload
            };
            this.productsAlreadyCreated = [...productsAlreadyCreated];
        },
        downloadJsonComponent() {
            downloadJson({
                payload: this.results,
                filename: "massiveLoadProductsResult"
            });
        }
    }
};
</script>
